/** @format */

import * as Sentry from "@sentry/react";
import { type UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { usePostHog } from "posthog-js/react";
import type { BrowseRunView, RunView } from "../pages/Run/grid/types";
import { queryClient } from "../queryClient";
import { trackEvent } from "../utils/analytics";
import galaxieClient, {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  isExpectedRequestError,
  isHttpError,
  setTokens,
} from "./galaxieClient";
import { useDriverIDtoVehicleID } from "./runsApi";

export type FavoriteDriverSurface = "profile" | "driver-details" | "scanner";
type FavoriteDriverDelta = "added" | "removed" | "set";

interface SaveFavoriteDriversArgs {
  favoriteDriverIDs: string[];
  surface: FavoriteDriverSurface;
  delta: FavoriteDriverDelta;
  driverId?: string;
}

const useSetUserContext = () => {
  const posthog = usePostHog();
  return (user: User) => {
    posthog.identify(user.id, {
      email: user.email,
      name: user.fullName,
      isStaff: user.isStaff,
      isIndustry: user.isIndustry,
      isAnonymous: false,
    });
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.fullName,
      isStaff: user.isStaff,
      isIndustry: user.isIndustry,
      isAnonymous: false,
    });
  };
};

export const useLogin = () => {
  return (email: string, password: string, rememberMe: boolean) =>
    galaxieClient
      .post(
        "/token/",
        { email, password },
        {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        }
      )
      .then(({ data: { access, refresh, user } }) => {
        if (rememberMe && typeof window !== "undefined") {
          // Only set in browser - SSR doesn't need to store tokens
          localStorage.setItem("accessToken", access as string);
          localStorage.setItem("refreshToken", refresh as string);
        }
        // Token will be automatically attached by request interceptor on subsequent requests
        return { user: user as User };
      });
};

export const useRunViews = () => {
  return useQuery<RunView[], Error>({
    queryKey: ["runViews"],
    queryFn: () =>
      galaxieClient
        .get("/run_views/", {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(response => response.data as RunView[]),
  });
};

export type BrowseRunViewsParams = {
  search?: string;
  // null/undefined = "any"; true/false = filter by owner.is_industry
  isIndustry?: boolean | null;
  page?: number;
};

export type BrowseRunViewsResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: BrowseRunView[];
};

export const useBrowseRunViews = (
  params: BrowseRunViewsParams,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery<BrowseRunViewsResponse, Error>({
    queryKey: ["runViewsBrowse", params],
    queryFn: () =>
      galaxieClient
        .get("/run_views/browse/", {
          params: {
            search: params.search ? params.search : undefined,
            is_industry: params.isIndustry == null ? undefined : params.isIndustry,
            page: params.page,
          },
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(response => response.data as BrowseRunViewsResponse),
    enabled,
    staleTime: 30_000,
  });
};

export const useRunViewById = (
  id: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery<RunView, Error>({
    queryKey: ["runView", id],
    queryFn: () =>
      galaxieClient
        .get(`/run_views/${id}/`, {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(response => response.data as RunView),
    enabled: enabled && !!id,
    staleTime: 30_000,
  });
};

export const useUpdateRunView = () => {
  return useMutation<RunView, Error, RunView, { previousViews: RunView[] }>({
    mutationFn: async (view: RunView) =>
      galaxieClient
        .patch(`/run_views/${view.id}/`, view, {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(response => response.data as RunView),
    onMutate: async (view: RunView) => {
      await queryClient.cancelQueries({ queryKey: ["runViews"] });
      const previousViews = queryClient.getQueryData<RunView[]>(["runViews"]) ?? [];

      // Optimistically update the cache
      queryClient.setQueryData<RunView[]>(["runViews"], old => {
        if (!old) {
          return [];
        }
        return old.map(v => (v.id === view.id ? view : v));
      });

      return { previousViews };
    },
    onSuccess: (updatedView, _variables, _context) => {
      // Update the specific view in the cache
      queryClient.setQueryData<RunView[]>(["runViews"], old => {
        if (!old) {
          return [];
        }
        return old.map(v => (v.id === updatedView.id ? updatedView : v));
      });
    },
    onError: (error, _variables, context) => {
      // Revert optimistic update on error
      if (context) {
        queryClient.setQueryData(["runViews"], context.previousViews);
      }
      Sentry.captureException(error);
    },
  });
};

export const useDeleteRunView = () => {
  return useMutation<void, Error, RunView, { previousViews: RunView[] }>({
    mutationFn: async (view: RunView) =>
      galaxieClient
        .delete(`/run_views/${view.id}/`, {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(() => undefined),
    onMutate: async (view: RunView) => {
      await queryClient.cancelQueries({ queryKey: ["runViews"] });
      const previousViews = queryClient.getQueryData<RunView[]>(["runViews"]) ?? [];

      // Optimistically remove the view from cache
      queryClient.setQueryData<RunView[]>(["runViews"], old => {
        if (!old) {
          return [];
        }
        return old.filter(v => v.id !== view.id);
      });

      return { previousViews };
    },
    onSuccess: (_data, deletedView, _context) => {
      // Ensure the view is removed from cache
      queryClient.setQueryData<RunView[]>(["runViews"], old => {
        if (!old) {
          return [];
        }
        return old.filter(v => v.id !== deletedView.id);
      });
    },
    onError: (error, _variables, context) => {
      // Revert optimistic update on error
      if (context) {
        queryClient.setQueryData(["runViews"], context.previousViews);
      }
      Sentry.captureException(error);
    },
  });
};

export const useCreateRunView = () => {
  return useMutation<RunView, Error, RunView, { previousViews: RunView[] }>({
    mutationFn: async (view: RunView) =>
      galaxieClient
        .post("/run_views/", view, {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(response => response.data as RunView),
    onMutate: async (view: RunView) => {
      await queryClient.cancelQueries({ queryKey: ["runViews"] });
      const previousViews = queryClient.getQueryData<RunView[]>(["runViews"]) ?? [];

      // Optimistically add the view to cache with a temporary ID
      const optimisticView = { ...view, id: `temp-${Date.now()}` };
      queryClient.setQueryData<RunView[]>(["runViews"], old => {
        if (!old) {
          return [optimisticView];
        }
        return [...old, optimisticView];
      });

      return { previousViews };
    },
    onSuccess: (createdView, _variables, _context) => {
      // Add the newly created view to cache
      queryClient.setQueryData<RunView[]>(["runViews"], old => {
        if (!old) {
          return [createdView];
        }
        // Remove any temporary views and add the real one
        const withoutTemps = old.filter(v => !v.id.startsWith("temp-"));
        return [...withoutTemps, createdView];
      });
    },
    onError: (error, _variables, context) => {
      // Revert optimistic update on error
      if (context) {
        queryClient.setQueryData(["runViews"], context.previousViews);
      }
      Sentry.captureException(error);
    },
  });
};

export const logout = () =>
  galaxieClient
    .post(
      "/auth/logout/",
      // Send the refresh token so the backend can blacklist it. `_retry`
      // skips the 401 refresh interceptor so a failed logout can never
      // reissue tokens mid-logout.
      { refresh: getRefreshToken() },
      {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
        _retry: true,
      } as AxiosRequestConfig & { _retry: boolean }
    )
    .then(response => response.data as User)
    .catch(() => undefined) // Logout is best-effort server-side
    .finally(() => {
      // Always clear client state — the user clicked log out, so log them out
      // locally regardless of the server response.
      clearTokens();
      void queryClient.invalidateQueries({
        queryKey: ["user"],
      });
    });

export const changePassword = (newPassword1: string, newPassword2: string) =>
  galaxieClient
    .post(
      "/auth/password/change/",
      {
        newPassword1,
        newPassword2,
      },
      {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
      }
    )
    .then(response => response.data as User);

export const resetPassword = (email: string) =>
  galaxieClient
    .post(
      "/auth/password/reset/",
      { email },
      {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
      }
    )
    .then(response => response.data as User);

export const resetPasswordConfirm = (
  uid: string,
  token: string,
  newPassword1: string,
  newPassword2: string
) =>
  galaxieClient
    .post(
      "/auth/password/reset/confirm/",
      {
        uid,
        token,
        newPassword1,
        newPassword2,
      },
      {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
      }
    )
    .then(response => response.data as User);

export const verifyEmail = (key: string) =>
  galaxieClient
    .post(
      "/auth/registration/verify-email/",
      { key },
      {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
      }
    )
    .then(({ data: { access, refresh, user } }) => {
      if (access && refresh) {
        setTokens(access as string, refresh as string);
      }
      if (user) {
        queryClient.setQueryData(["user"], user);
      }
      return user as User;
    });

export const signUp = (
  email: string,
  password1: string,
  password2: string,
  fullName: string,
  shortName: string
) =>
  galaxieClient
    .post(
      "/auth/registration/",
      { email, password1, password2, fullName, shortName },
      {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
      }
    )
    .then(({ data: user }: { data: User }) => {
      queryClient.setQueryData(["user"], user);
      return user;
    });

export const getUser = () =>
  galaxieClient
    .get("/auth/user/", {
      headers: {
        "Cache-Control": "no-cache, no-store",
      },
    })
    .then(response => response.data as User);

export function useUser<TQueryFnData = User>(
  options = {}
): UseQueryResult<TQueryFnData, Error> {
  const setUserContext = useSetUserContext();
  return useQuery<User, Error, TQueryFnData>({
    queryKey: ["user"],
    queryFn: async () => {
      if (!getAccessToken()) {
        return {} as User;
      }
      return await getUser()
        .then(user => {
          if (user?.id) {
            setUserContext(user);
          }
          return user;
        })
        .catch((error: unknown) => {
          // Don't log 401 errors to Sentry - these are expected when user isn't authenticated
          // Fixes JAVASCRIPT-REACT-E4: HTTP 401 errors being reported unnecessarily
          const is401 =
            error &&
            typeof error === "object" &&
            "status" in error &&
            (error as { status: number }).status === 401;

          // Log non-401 errors to Sentry in production for visibility
          if (!is401 && !import.meta.env.DEV && import.meta.env.VITE_LOCAL !== "True") {
            Sentry.captureException(error, {
              tags: { errorType: "user_query_error" },
            });
          }
          // Log to console in development for debugging
          if (import.meta.env.DEV) {
            console.error("Failed to get user:", error);
          }
          return {} as User;
        });
    },
    refetchOnWindowFocus: false,
    refetchInterval: 1000 * 60 * 60,
    ...options,
  });
}

export const useUpdateUser = () =>
  useMutation({
    mutationFn: async (data: User) => await updateUser(data),
    onMutate: async (data: User) => {
      const previousUser: User | undefined = queryClient.getQueryData(["user"]);
      if (!previousUser) {
        // Handle the case when previousUser is undefined
        // For example, you can assign a default value or throw an error
        throw new Error("Previous user is undefined.");
      }
      await queryClient.cancelQueries({ queryKey: ["user"] });
      queryClient.setQueryData(["user"], {
        ...previousUser,
        ...(data as unknown as object),
      });
      return { previousUser };
    },
    onSuccess: (data, _variables, _context) => {
      queryClient.setQueryData(["user"], data);
      return data;
    },
    onError: (error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(["user"], context.previousUser);
        return context.previousUser;
      } else {
        return error;
      }
    },
  });

export const useFavoriteVehicleIDsForRun = (runID: string, options = {}) => {
  const { data: driverIDtoVehicleID, isSuccess } = useDriverIDtoVehicleID(runID, {
    staleTime: 10000,
  });
  return useUser<string[]>({
    select: ({ favoriteDrivers }: { favoriteDrivers: string[] }) => {
      if (!driverIDtoVehicleID || !favoriteDrivers) {
        return [] as string[];
      }
      const faves = favoriteDrivers
        ?.map(driverID => driverIDtoVehicleID?.[driverID])
        ?.filter(a => a);
      return faves;
    },
    staleTime: 10000, // Consider data fresh for 10 seconds
    ...options,
    enabled: isSuccess,
  });
};

const useSaveFavoriteDrivers = () =>
  useMutation({
    mutationFn: async ({ favoriteDriverIDs }: SaveFavoriteDriversArgs) =>
      galaxieClient
        .patch(
          `/auth/user/`,
          { favoriteDrivers: favoriteDriverIDs },
          {
            headers: {
              "Cache-Control": "no-cache, no-store",
            },
          }
        )
        .then(res => res.data as User),
    onMutate: async ({ favoriteDriverIDs }: SaveFavoriteDriversArgs) => {
      const previousUser: User | undefined = queryClient.getQueryData(["user"]);
      if (!previousUser) {
        throw new Error("Previous user is undefined.");
      }
      await queryClient.cancelQueries({ queryKey: ["user"] });
      queryClient.setQueryData(["user"], {
        ...previousUser,
        favoriteDrivers: favoriteDriverIDs,
      });
      return { previousUser };
    },
    onSuccess: (data, variables, _context) => {
      queryClient.setQueryData(["user"], data);
      const count = data?.favoriteDrivers?.length ?? variables.favoriteDriverIDs.length;
      const eventName =
        variables.delta === "added"
          ? "favorite_driver_added"
          : variables.delta === "removed"
            ? "favorite_driver_removed"
            : "favorite_drivers_set";
      trackEvent(eventName, {
        surface: variables.surface,
        count,
        ...(variables.driverId ? { driverId: variables.driverId } : {}),
      });
    },
    onError: (_error, _variables, context) => {
      if (context) {
        queryClient.setQueryData(["user"], context.previousUser);
      }
    },
  });

export const useAddFavoriteDriver = () => {
  const { data: { favoriteDrivers = [] } = {} } = useUser();
  const { mutate: saveFavoriteDrivers } = useSaveFavoriteDrivers();
  return (favoriteDriverId: string, surface: FavoriteDriverSurface) => {
    const driverIDs = [...favoriteDrivers, favoriteDriverId];
    return saveFavoriteDrivers({
      favoriteDriverIDs: driverIDs,
      surface,
      delta: "added",
      driverId: favoriteDriverId,
    });
  };
};

export const useRemoveFavoriteDriver = () => {
  const { data: { favoriteDrivers = [] } = {} } = useUser();
  const { mutate: saveFavoriteDrivers } = useSaveFavoriteDrivers();
  return (favoriteDriverId: string, surface: FavoriteDriverSurface) => {
    const driverIDs = favoriteDrivers.filter(
      (driverId: string) => driverId !== favoriteDriverId
    );
    return saveFavoriteDrivers({
      favoriteDriverIDs: driverIDs,
      surface,
      delta: "removed",
      driverId: favoriteDriverId,
    });
  };
};

export const updateUser = (data: User) =>
  galaxieClient
    .patch(`/auth/user/`, data, {
      headers: {
        "Cache-Control": "no-cache, no-store",
      },
    })
    .then(response => {
      queryClient.setQueryData(["user"], response.data);
      return response.data as User;
    });

// Legacy hooks for backward compatibility with ConfigurableGridWrapper
export const useAddView = () => useCreateRunView();
export const useRemoveView = () => useDeleteRunView();
export const useUpdateView = () => useUpdateRunView();

export const useForkView = () => {
  return useMutation<
    RunView[],
    Error,
    { viewId: string; name?: string },
    { previousViews: RunView[] }
  >({
    mutationFn: async ({ viewId, name }) =>
      galaxieClient
        .post(
          `/run_views/${viewId}/fork/`,
          { name },
          {
            headers: {
              "Cache-Control": "no-cache, no-store",
            },
          }
        )
        .then(response => response.data as RunView[]),
    onMutate: async variables => {
      const { viewId, name } = variables;
      await queryClient.cancelQueries({ queryKey: ["runViews"] });
      const previousViews = queryClient.getQueryData<RunView[]>(["runViews"]) ?? [];

      // Find the original view to create an optimistic fork
      const originalView = previousViews.find((view: RunView) => view.id === viewId);
      if (originalView) {
        const optimisticFork = {
          ...originalView,
          id: `temp-fork-${Date.now()}`,
          name: name ?? `${originalView.name} (Copy)`,
          slug: `${originalView.slug}-copy-${Date.now()}`,
          isSystemDefault: false,
          source: viewId,
          canonicalPosition: originalView.canonicalPosition, // Maintain original position
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        queryClient.setQueryData<RunView[]>(["runViews"], old => {
          if (!old) {
            return [optimisticFork];
          }
          return [...old, optimisticFork];
        });
      }

      return { previousViews };
    },
    onSuccess: (data, _variables, _context) => {
      queryClient.setQueryData(["runViews"], data);
      return data;
    },
    onError: (error, _variables, context) => {
      // Revert optimistic update on error
      if (context) {
        queryClient.setQueryData(["runViews"], context.previousViews);
      }
      // A 404 means the source view was deleted or stale between page load and
      // the fork click (a benign race), not a bug. Refetch to resync the client
      // and skip the Sentry capture — this was JAVASCRIPT-REACT-F1. Use the
      // duck-typed guard (not `instanceof HttpError`) so a bundle-duplicated
      // error identity can't slip past and still get captured.
      if (isHttpError(error) && error.status === 404) {
        void queryClient.invalidateQueries({ queryKey: ["runViews"] });
        return;
      }
      // Offline / slow connection — transient, not an actionable bug.
      if (isExpectedRequestError(error)) {
        return;
      }
      Sentry.captureException(error);
    },
  });
};
export const useUpdateViews = () => {
  return useMutation<RunView[], Error, RunView[], { previousViews: RunView[] }>({
    mutationFn: async (views: RunView[]) => {
      // Use the bulk_position endpoint for efficiency
      const positionData = views.map((view, index) => ({
        view_id: view.id,
        position: index,
        is_hidden: false,
      }));

      await galaxieClient.post("/run_views/bulk_position/", positionData, {
        headers: {
          "Cache-Control": "no-cache, no-store",
        },
      });

      // Return the updated views list
      return galaxieClient
        .get("/run_views/", {
          headers: {
            "Cache-Control": "no-cache, no-store",
          },
        })
        .then(response => response.data as RunView[]);
    },
    onMutate: async (views: RunView[]) => {
      await queryClient.cancelQueries({ queryKey: ["runViews"] });
      const previousViews = queryClient.getQueryData<RunView[]>(["runViews"]) ?? [];

      // Optimistically update the cache
      queryClient.setQueryData<RunView[]>(["runViews"], views);

      return { previousViews };
    },
    onSuccess: (data, _variables, _context) => {
      queryClient.setQueryData(["runViews"], data);
    },
    onError: (error, _variables, context) => {
      // Revert optimistic update on error
      if (context) {
        queryClient.setQueryData(["runViews"], context.previousViews);
      }
      Sentry.captureException(error);
    },
  });
};
